import React, { useRef, useState } from "react";
import { m as motion } from "../../shared/components/Motion";
import emailjs from "@emailjs/browser";
import "./contact.css";
import ContactCard from "./ContactCard";
import ContactFormInput from "./ContactFormInput";
import ContactSkeleton from "./ContactSkeleton";
import { useContactData } from "../../shared/hooks";
import { useAdmin } from "../../features/admin/AdminProvider";
import EditContactModal from "../../features/admin/modals/EditContactModal";
import {
  containerVariants,
  childVariantsMedium as childVariants,
} from "../../shared/utils";

const Contact = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const { data, loading } = useContactData();
  const { adminMode, isAdmin, contactDraft } = useAdmin();
  const form = useRef();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [, setEditingData] = useState(null);

  // Use draft ONLY if admin is authenticated, otherwise use published data
  const displayData = isAdmin && contactDraft ? contactDraft : data || {};

  const sendEmail = (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    emailjs
      .sendForm("service_kkez89e", "template_434hi8a", form.current, {
        publicKey: "d7-sgfMZaY11mOF61",
      })
      .then(
        () => {
          e.target.reset();
          setSubmitted(true);
          setTimeout(() => {
            setSubmitted(false);
          }, 3000);
        },
        (error) => {
          console.error("Email sending failed:", error);
          setSubmitted(false);
        },
      )
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const handleEditContact = (type) => {
    setEditingType(type);
    setEditingData(displayData?.[type] || { value: "", link: "" });
    setIsModalOpen(true);
  };

  const handleSaveContact = () => {
    setIsModalOpen(false);
    setEditingType(null);
    setEditingData(null);
  };

  // Show skeleton while loading
  if (loading || !displayData?.email) {
    return <ContactSkeleton />;
  }

  return (
    <>
      <motion.section
        className="contact section"
        id="contact"
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        <motion.h2 className="section__title" variants={childVariants}>
          Get in touch
        </motion.h2>
        <motion.span className="section__subtitle" variants={childVariants}>
          Contact Me
        </motion.span>

        <motion.div
          className="contact__container container grid"
          variants={containerVariants}
        >
          {/* Left: Talk to me */}
          <motion.div className="contact__content" variants={childVariants}>
            <h3 className="contact__title">Talk to me</h3>
            <div className="contact__info">
              {displayData?.email && (
                <ContactCard
                  title="Email"
                  data={displayData.email.value}
                  link={displayData.email.link}
                  iconClass="bx bx-mail-send contact__card-icon"
                  isAdmin={adminMode}
                  onEdit={() => handleEditContact("email")}
                />
              )}
              {displayData?.whatsapp && (
                <ContactCard
                  title="Whatsapp"
                  data={displayData.whatsapp.value}
                  link={displayData.whatsapp.link}
                  iconClass="bx bxl-whatsapp contact__card-icon"
                  isAdmin={adminMode}
                  onEdit={() => handleEditContact("whatsapp")}
                />
              )}
              {displayData?.messenger && (
                <ContactCard
                  title="Messenger"
                  data={displayData.messenger.value}
                  link={displayData.messenger.link}
                  iconClass="bx bxl-messenger contact__card-icon"
                  isAdmin={adminMode}
                  onEdit={() => handleEditContact("messenger")}
                />
              )}
            </div>
          </motion.div>
          <motion.div className="contact__content" variants={childVariants}>
            <h3 className="contact__title">Write me your project</h3>

            <form ref={form} onSubmit={sendEmail} className="contact__form">
              <ContactFormInput
                label="Name"
                type="text"
                name="name"
                placeholder="Enter your name"
              />
              <ContactFormInput
                label="Mail"
                type="email"
                name="email"
                placeholder="Enter your email"
              />
              <div className="contact__form-div contact__form-area">
                <label htmlFor="project-input" className="contact__form-tag">
                  Project
                </label>
                <textarea
                  name="project"
                  cols="30"
                  rows="10"
                  id="project-input"
                  className="contact__form-input"
                  placeholder="Write your project"
                  required
                ></textarea>
              </div>{" "}
              <button
                type="submit"
                className="button button--flex"
                style={{
                  padding: "0.90rem 2rem",
                }}
              >
                {isSubmitting
                  ? "Sending..."
                  : submitted
                    ? "Sent"
                    : "Send Message"}
                {!isSubmitting && !submitted && (
                  <i
                    style={{ fontSize: "1.5rem", marginLeft: "0.5rem" }}
                    className="uil uil-message"
                  ></i>
                )}
              </button>
            </form>
          </motion.div>
        </motion.div>
      </motion.section>

      <EditContactModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        contactData={displayData}
        onSave={handleSaveContact}
        contactType={editingType}
        publishedData={data}
      />
    </>
  );
};

export default Contact;
